import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { DEFAULT_LESSON_COLOR } from '@diana/shared'
import { api, type TagInUse } from '../../lib/api.js'
import { ColorDot, ColorPicker } from '../../components/ColorPicker.js'
import { Button, ErrorLine, Rows } from '../../classroom/ui.js'

/**
 * Tags, and what colour each one is drawn in.
 *
 * Tags are typed on the student, not created here — this screen only paints
 * them. It lists every tag anyone carries alongside every tag that already
 * has a colour, so a tag she typed weeks ago can be given one without being
 * retyped.
 */
export function TagsManager({ tags }: { tags: TagInUse[] }) {
  const queryClient = useQueryClient()

  const refresh = () => {
    for (const key of [['studio', 'tags'], ['studio', 'clients'], ['studio', 'calendar'], ['studio', 'accounting']]) {
      queryClient.invalidateQueries({ queryKey: key })
    }
  }

  const coloured = tags.filter((t) => t.color)
  const plain = tags.filter((t) => !t.color)

  return (
    <section className="flex flex-col gap-8">
      <p className="m-0 max-w-[680px] text-class-ink-soft">
        Tag opisuje ucznia — „premium”, „speak and roll”. Nadaj mu kolor, a lekcje tego ucznia będą w tym kolorze w
        kalendarzu. Jeśli uczeń ma kilka kolorowych tagów, liczy się{' '}
        <strong className="font-semibold text-class-ink">pierwszy z jego listy</strong>.
      </p>

      {tags.length === 0 ? (
        <p className="m-0 text-class-ink-soft">
          Nie masz jeszcze żadnych tagów. Dopisz je przy uczniu, a potem wróć tu po kolory.
        </p>
      ) : (
        <>
          {coloured.length > 0 && (
            <Rows>
              {coloured.map((tag) => (
                <TagRow key={tag.tag} tag={tag} onChanged={refresh} />
              ))}
            </Rows>
          )}

          {plain.length > 0 && (
            <div className="flex flex-col">
              <h3 className="m-0 pb-3 text-class-label font-semibold text-class-ink-faint">Bez koloru</h3>
              <Rows>
                {plain.map((tag) => (
                  <TagRow key={tag.tag} tag={tag} onChanged={refresh} />
                ))}
              </Rows>
            </div>
          )}
        </>
      )}
    </section>
  )
}

function TagRow({ tag, onChanged }: { tag: TagInUse; onChanged: () => void }) {
  const [renaming, setRenaming] = useState(false)
  const [name, setName] = useState(tag.tag)

  const setColor = useMutation({
    mutationFn: (hex: string) => api.studio.setTagColor(tag.tag, hex),
    onSuccess: onChanged,
  })

  const rename = useMutation({
    mutationFn: (to: string) => api.studio.renameTag(tag.tag, to),
    onSuccess: () => {
      setRenaming(false)
      onChanged()
    },
  })

  const submitRename = () => {
    const next = name.trim().toLowerCase()
    if (!next || next === tag.tag) return setRenaming(false)
    rename.mutate(next)
  }

  const cancelRename = () => {
    setName(tag.tag)
    setRenaming(false)
  }

  return (
    <div className="flex flex-col gap-3 py-4">
      <div className="flex min-h-10 flex-wrap items-center gap-x-5 gap-y-2">
        {renaming ? (
          <span className="flex flex-wrap items-center gap-3">
            <label className="sr-only" htmlFor={`tag-${tag.tag}`}>
              Nowa nazwa tagu „{tag.tag}”
            </label>
            <input
              id={`tag-${tag.tag}`}
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') submitRename()
                if (e.key === 'Escape') cancelRename()
              }}
              autoFocus
              className="cl-input w-56 max-w-full"
            />
            <Button variant="secondary" onClick={submitRename} disabled={rename.isPending} className="min-h-10 px-4 text-class-gloss">
              {rename.isPending ? 'Zmieniam…' : 'Zapisz'}
            </Button>
            <button type="button" onClick={cancelRename} className="cl-link-quiet text-class-gloss">
              Anuluj
            </button>
          </span>
        ) : (
          <>
            <span className="inline-flex items-center gap-2 font-semibold">
              {tag.color ? (
                <ColorDot color={tag.color} size={10} />
              ) : (
                <span aria-hidden className="inline-block h-2.5 w-2.5 rounded-full shadow-[inset_0_0_0_1px_var(--color-class-ink-faint)]" />
              )}
              {tag.tag}
            </span>
            <button type="button" onClick={() => setRenaming(true)} className="cl-link text-class-gloss">
              Zmień nazwę
            </button>
          </>
        )}

        <span className="ml-auto flex items-center gap-5">
          {!tag.inUse && <span className="text-class-label text-class-ink-faint">nikt go nie ma</span>}
          {tag.color && (
            <button type="button" onClick={() => setColor.mutate('')} className="cl-link-quiet text-class-gloss">
              Zdejmij kolor
            </button>
          )}
        </span>
      </div>

      <ColorPicker classroom value={tag.color} onChange={(hex) => setColor.mutate(hex)} fallback={DEFAULT_LESSON_COLOR} />

      {rename.isSuccess && rename.data.clients > 0 && (
        <p className="m-0 text-class-label font-semibold text-class-correct">
          ✓ Zmieniono u {rename.data.clients} {rename.data.clients === 1 ? 'ucznia' : 'uczniów'}.
        </p>
      )}
      {(setColor.isError || rename.isError) && <ErrorLine>Nie udało się zapisać.</ErrorLine>}
    </div>
  )
}
